import { Paper, Skeleton, Stack, SvgIconProps, Typography } from '@mui/material';
import React from 'react';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';

const SKELETON_ROW_COUNT = 3;
const SKELETON_ROW_HEIGHT = 34;

export interface EmptyStateProps {
  icon?: React.ComponentType<SvgIconProps>;
  title: string;
  content: string;
}

interface Props {
  header: string;
  rows: RowsProps[];
  isLoading: boolean;
  emptyState?: EmptyStateProps;
}

export interface RowsProps {
  Icon: React.ComponentType<SvgIconProps>;
  text: string;
  color: string;
  iconColor?: string;
  textColor?: string;
  onClick?: () => void;
}

const Card: React.FC<Props> = ({ header, isLoading, rows, emptyState }) => {
  return (
    <Paper elevation={3} sx={{ height: '100%' }}>
      <Stack padding={2} gap={2} width="100%">
        <Typography variant="subtitle2">{header}</Typography>
        {isLoading ? (
          <Stack gap={0.5}>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
              <Skeleton key={index} variant="rectangular" height={SKELETON_ROW_HEIGHT} />
            ))}
          </Stack>
        ) : emptyState ? (
          <EmptyStateOverview
            icon={emptyState.icon}
            title={emptyState.title}
            titleVariant="subtitle2"
            titleColor="textSecondary"
            content={emptyState.content}
            contentVariant="body2"
            sx={{ height: 'auto', py: 4 }}
          />
        ) : (
          <Stack flexDirection="column" gap={0.5}>
            {rows.map((row) => (
              <Stack
                key={row.text}
                bgcolor={row.color}
                flexDirection="row"
                paddingLeft={2}
                paddingRight={2}
                paddingY={1}
                gap={1}
                minHeight={SKELETON_ROW_HEIGHT}
                alignItems="center"
                onClick={row.onClick}
                sx={{ cursor: row.onClick ? 'pointer' : 'default' }}
              >
                <row.Icon sx={{ flexShrink: 0, color: row.iconColor }} />
                <Typography variant="body2" sx={{ color: row.textColor }}>
                  {row.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default Card;
